// tailSales.js — pick which sim-log entries still need a sale event recorded.
//
// Simulate-to-End resolves the rest of the draft headlessly and then has to
// replay that tail into the draft-log recorder, because the live sale hook
// (processSoldPlayer) is bypassed entirely on that path. The question this
// module answers is exactly one thing: of everything the sim knows about, which
// picks has the recorder NOT already seen?
//
// It is deliberately answered by IDENTITY (pick number), never by position
// (slice/count). The sim log's ordering is conditional — see selectTailSales —
// and a positional selector silently trims the wrong end whenever that
// condition flips. Identity is correct under any ordering, which is the whole
// point of this file existing.
//
// Zero dependencies: no React, no engine, no network. Pure functions over plain
// data, so the same code the hook ships can be exercised directly in a test.

/**
 * The set of pick numbers a recorder has already logged a `sale` for.
 *
 * @param {Object} log Recorder from createDraftLog (or null/undefined).
 * @returns {Set<number>} Pick numbers already recorded. Empty when there is no
 *                        recorder, which makes the caller a no-op rather than a
 *                        crash — logging is best-effort telemetry, never a
 *                        reason to break a draft.
 */
export function recordedSalePicks(log) {
  const events = log && Array.isArray(log.state?.events) ? log.state.events : [];
  const picks = new Set();
  events.forEach((event) => {
    if (event && event.type === 'sale') picks.add(event.pick);
  });
  return picks;
}

/**
 * The sim-log entries whose pick is not yet in the recorder, ascending by pick.
 *
 * Order-independence is the contract. `simLog` arrives in one of two orderings
 * depending on a branch the caller took:
 *   - oldest-first, when the draft was already full and simulateDraftSync was
 *     skipped (the log is then the reversed mockDraftLog plus local pushes)
 *   - newest-first, when simulateDraftSync ran — it ends with draftLog.reverse()
 *     to match mockDraftLog's React-state shape
 * Selecting by count instead of identity is what caused the jul29 corruption:
 * with N picks played live it dropped the LAST N picks of the draft and replayed
 * the first N, which were already recorded. Filtering on membership is immune to
 * that, and stays immune if either branch changes again.
 *
 * A pick already emitted within this same pass is also excluded, so a sim log
 * that itself carried a duplicate cannot produce two sale events either.
 *
 * @param {Array}       simLog        Entries shaped {pickNumber, player, price, winnerId}.
 * @param {Set<number>} recordedPicks Pick numbers already logged (see recordedSalePicks).
 * @returns {Array} The subset to replay, sorted ascending by pickNumber so the
 *                  stored event stream reads chronologically.
 */
export function selectTailSales(simLog, recordedPicks) {
  const seen = new Set(recordedPicks || []);
  const tail = [];
  (Array.isArray(simLog) ? simLog : []).forEach((entry) => {
    if (!entry || seen.has(entry.pickNumber)) return;
    seen.add(entry.pickNumber);
    tail.push(entry);
  });
  tail.sort((a, b) => a.pickNumber - b.pickNumber);
  return tail;
}
